import * as preact from "preact";
import * as _ from "underscore";

import BookModel from "models/BookModel";
import models from "models/models";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as textToSpeech from "textToSpeech";
import BookCoverConnector from "views/BookCoverConnector";
import EmailReminderAndStreakRepairView from "views/EmailReminderAndStreakRepairView";
import WalkthroughChecklistView from "views/WalkthroughChecklistView";
import * as walkthrough from "walkthrough";

interface Props {
  appRouter: any;
  options?: object;
}

interface DashboardData {
  showPracticeGraph?: boolean;
  streak?: {
    days: number;
    plural?: boolean;
  };
  karma?: {
    booksShared: number;
    booksSharedPlural?: boolean;
    karmaScore?: number;
  };
  conversationButton?: boolean;
  flashcardOrConversation?: boolean;
  flashcardReminder?: boolean;
  showRow?: boolean;
  practiceUrl?: string;
  speakingSupported?: boolean;
  continueReading?: any;
  newStreak?: {
    streak: number;
    goalMetToday: boolean;
    heading?: string;
    recentStats: Array<{
      date: string;
      dayOfWeek: string;
      wordsRead: number;
      wordsPracticed: number;
      practiceWordsReadEquivalent: number;
      wordsPerDayGoal: number;
      goalMet: boolean;
      wordsReadHeight?: number;
      wordsPracticedHeight?: number;
      wordsPerDayGoalHeight?: number;
      emptyHeight?: number;
      dateString?: string;
      rightHandSide?: boolean;
    }>;
    recentStatsExist?: boolean;
    wordsPerDayGoalHeight?: number;
  };
  wordHistory?: {
    today: {
      notStarted: boolean;
      goalReached: boolean;
      wordsNeeded: number;
      total: number;
    };
    progressBars: Array<{ classes: string }>;
    streak: {
      days: number;
    };
  };
  showForumCallout?: boolean;
  user?: any;
}

type State =
  | {
      type: "fetching";
    }
  | {
      type: "fetched";
      data: DashboardData;
    };

class DashboardPageView extends preact.Component<Props, State> {
  private walkthroughChecklistView?: any;
  private emailReminderOptInView?: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      type: "fetching",
    };

    this.fetchData();

    models.user.on("change:currentLearningLanguage", this.onLanguageChange);
  }

  onLanguageChange = async () => {
    await this.setState({
      type: "fetching",
    });
    this.fetchData();
  };

  async fetchData() {
    localStorage.setItem("libraryOrDashboardPath", "/dashboard");

    // Fetch dashboard data
    const dashboardData: DashboardData = (
      await readlangClient.request("GET", "/api/dashboard", {
        timezoneOffset: dateUtils.getTimezoneOffset(),
        language: models.user.get("currentLearningLanguage"),
      })
    ).data;

    // Process dashboard data
    if (walkthrough.hasAchievement("translate")) {
      dashboardData.showPracticeGraph = true;
    }

    if (dashboardData.streak && dashboardData.streak.days > 1) {
      dashboardData.streak.plural = true;
    }

    if (dashboardData.karma) {
      dashboardData.karma.booksSharedPlural =
        dashboardData.karma.booksShared > 1 ? true : false;
    }

    if (models.user.hasChatFeature()) {
      dashboardData.conversationButton = true;
    }

    dashboardData.flashcardOrConversation =
      dashboardData.flashcardReminder || dashboardData.conversationButton;

    dashboardData.showRow =
      dashboardData.continueReading || dashboardData.flashcardOrConversation
        ? true
        : false;
    dashboardData.practiceUrl = "/flashcards";

    if (
      textToSpeech.languageSupported(models.user.get("currentLearningLanguage"))
    ) {
      dashboardData.speakingSupported = true;
    }

    if (dashboardData.newStreak) {
      if (dashboardData.newStreak.streak === 0) {
        dashboardData.newStreak.heading = `Start A Streak`;
      } else if (dashboardData.newStreak.goalMetToday) {
        dashboardData.newStreak.heading = `You Have A ${dashboardData.newStreak.streak} Day Streak!`;
      } else if (new Date().getHours() < 17) {
        dashboardData.newStreak.heading = `Keep Up Your ${dashboardData.newStreak.streak} Day Streak`;
      } else {
        dashboardData.newStreak.heading = `Your ${dashboardData.newStreak.streak} Day Streak Is In Danger`;
      }

      const maxWordsReadEquivalent = _.max(
        dashboardData.newStreak.recentStats.map(
          (recentDayStats) =>
            recentDayStats.wordsPracticed *
              recentDayStats.practiceWordsReadEquivalent +
            recentDayStats.wordsRead
        )
      );

      dashboardData.newStreak.recentStatsExist = maxWordsReadEquivalent > 0;

      let maxYAxis = _.max([
        maxWordsReadEquivalent,
        ...dashboardData.newStreak.recentStats.map(
          (recentDayStats) => recentDayStats.wordsPerDayGoal
        ),
      ]);

      // Round up to nearest 250 words
      maxYAxis = Math.ceil(maxYAxis / 250) * 250;

      for (const [
        index,
        recentDayStats,
      ] of dashboardData.newStreak.recentStats.entries()) {
        recentDayStats.wordsReadHeight =
          (recentDayStats.wordsRead / maxYAxis) * 100;
        recentDayStats.wordsPracticedHeight =
          ((recentDayStats.wordsPracticed *
            recentDayStats.practiceWordsReadEquivalent) /
            maxYAxis) *
          100;
        recentDayStats.wordsPerDayGoalHeight =
          (recentDayStats.wordsPerDayGoal / maxYAxis) * 100 - 1;
        recentDayStats.emptyHeight =
          100 -
          recentDayStats.wordsReadHeight -
          recentDayStats.wordsPracticedHeight;

        // It's important to use `/` instead of `-` because of the way that JavaScript handles
        // dates. If we use `-`, then the date will be interpreted as UTC, and the date will be
        // off by one day.
        const date = new Date(recentDayStats.date.replace(/-/g, "/"));
        recentDayStats.dateString = `${dateUtils.dayOfWeekString(
          date
        )} ${date.getDate()} ${dateUtils.monthString(date)}`;

        recentDayStats.rightHandSide = index > 3;
      }

      // XXX Making the assumption that the goal is the same every day.
      dashboardData.newStreak.wordsPerDayGoalHeight =
        dashboardData.newStreak.recentStats[0].wordsPerDayGoalHeight;
    }

    dashboardData.showForumCallout =
      !models.user.has("forumAccess") && models.user.hasPremiumAccess();

    dashboardData.user = models.user.viewJSON();

    this.setState({
      type: "fetched",
      data: dashboardData,
    });
  }

  componentDidMount() {
    if (this.state.type === "fetched") {
      this.setupSubviews();
    }
  }

  componentDidUpdate() {
    if (this.state.type === "fetched") {
      this.setupSubviews();
    }
  }

  componentWillUnmount() {
    this.cleanupSubviews();
    models.user.off("change:currentLearningLanguage", this.onLanguageChange);
  }

  setupSubviews() {
    if (this.state.type !== "fetched") return;

    const { data } = this.state;

    // Setup walkthrough checklist
    const walkthroughElement = document.querySelector(
      ".walkthroughChecklistView"
    );
    if (walkthroughElement) {
      this.walkthroughChecklistView = new WalkthroughChecklistView({
        models: models,
        appRouter: this.props.appRouter,
        el: walkthroughElement,
      });
    }

    // Setup continue reading book cover
    if (data.continueReading) {
      const bookCoverElement = document.querySelector(
        ".continueReading .bookCover"
      );
      if (bookCoverElement) {
        const bookModel = new BookModel(data.continueReading);
        preact.render(
          preact.h(BookCoverConnector, {
            book: bookModel,
            userID: models.user.id as string,
            compactMode: true,
          }),
          bookCoverElement
        );
      }
    }

    // Setup email reminder opt-in
    if (data.newStreak) {
      const emailReminderElement = document.querySelector(
        "#emailReminderOptIn"
      );
      if (emailReminderElement) {
        this.emailReminderOptInView = new EmailReminderAndStreakRepairView({
          models: models,
          el: emailReminderElement,
        });
      }
    }
  }

  cleanupSubviews() {
    if (this.walkthroughChecklistView) {
      this.walkthroughChecklistView.remove();
      this.walkthroughChecklistView = undefined;
    }
    if (this.emailReminderOptInView) {
      this.emailReminderOptInView.remove();
      this.emailReminderOptInView = undefined;
    }
  }

  render() {
    if (this.state.type === "fetching") {
      return <div>Loading...</div>;
    }

    const { data } = this.state;

    return (
      <div>
        <div class="dashboardPanel walkthroughChecklistView"></div>

        {data.showForumCallout && (
          <div class="forumCallout callout">
            Say hello, suggest features, report bugs and join discussions on the{" "}
            <a href="https://forum.readlang.com" target="_blank">
              Readlang Forum
            </a>
            .
          </div>
        )}

        {data.showRow && (
          <div class="dashboardRow">
            {data.continueReading && (
              <div class="dashboardPanel continueReading">
                <h2 style="margin-bottom:15px;">Continue Reading</h2>
                <p>
                  <div class="bookCover"></div>
                </p>
                <p style="text-align:right;margin-bottom:0">
                  <a href="/myTexts">Manage my queue →</a>
                </p>
              </div>
            )}

            {data.flashcardOrConversation && (
              <div class="dashboardPanel practiceFlashcards">
                <h2>Practice</h2>

                {data.flashcardReminder && (
                  <a class="startFlashcardsButton" href={data.practiceUrl}>
                    <img src="/content/flashcardsIcon.svg" />
                    <span class="buttonTitle">Start Practice</span>
                  </a>
                )}
                {data.conversationButton && (
                  <a class="startChatButton" href="/chat/start">
                    <img src="/content/chat2.svg" />
                    <span class="buttonTitle">Start Conversation</span>
                  </a>
                )}
                {data.flashcardReminder && (
                  <p style="text-align:right;margin-bottom:0">
                    <a href="/learn">Customize practice →</a>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {data.newStreak && (
          <div class="dashboardPanel" style="text-align:center">
            <h2>{data.newStreak.heading}</h2>
            <p>
              Read 500 words or practice 10 flashcards every day to keep your
              streak going.
            </p>
            <div class="streakPanelContainer">
              <div class="streakPanel">
                {data.newStreak.recentStatsExist && (
                  <div class="barChartLabels">
                    <div class="barChartLabel">
                      <span class="wordsReadLabelSquare"></span>
                      <span class="label">
                        Words <br class="mobileOnly" />
                        Read
                      </span>
                    </div>
                    <div class="barChartLabel">
                      <span class="wordsPracticedLabelSquare"></span>
                      <span class="label">
                        Words <br class="mobileOnly" />
                        Practiced
                      </span>
                    </div>
                    <div class="barChartLabel">
                      <span class="goalLineLabelExample"></span>
                      <span class="label">
                        Daily <br class="mobileOnly" />
                        Goal
                      </span>
                    </div>
                  </div>
                )}
                <div class="barChart">
                  {data.newStreak.recentStatsExist && (
                    <>
                      <div
                        class="goalLine"
                        style={`bottom: ${data.newStreak.wordsPerDayGoalHeight}px`}
                      ></div>
                      <div class="bottomLine"></div>
                      <table>
                        <tr>
                          {data.newStreak.recentStats.map((stat) => (
                            <td class="barCell" key={stat.date}>
                              <div
                                class="emptyBar"
                                style={`height: ${stat.emptyHeight}px`}
                              ></div>
                              <div
                                class="wordsPracticedBar"
                                style={`height: ${stat.wordsPracticedHeight}px`}
                              ></div>
                              <div
                                class="wordsReadBar"
                                style={`height: ${stat.wordsReadHeight}px`}
                              ></div>
                              <div
                                class={`barCellTooltip${
                                  stat.rightHandSide ? " right" : ""
                                }`}
                              >
                                <h3>{stat.dateString}</h3>
                                <div class="content">
                                  Words Read: {stat.wordsRead}
                                  <br />
                                  Words Practiced: {stat.wordsPracticed}
                                </div>
                              </div>
                            </td>
                          ))}
                        </tr>
                      </table>
                    </>
                  )}
                </div>
                <table>
                  <tr>
                    {data.newStreak.recentStats.map((stat) => (
                      <th key={`${stat.date}-day`}>{stat.dayOfWeek}</th>
                    ))}
                  </tr>
                  <tr>
                    {data.newStreak.recentStats.map((stat) => (
                      <td key={`${stat.date}-fire`}>
                        <img
                          class={`fireIcon ${stat.goalMet ? "goalMet" : ""}`}
                          src="/content/fire-icon.svg"
                        />
                      </td>
                    ))}
                  </tr>
                </table>
              </div>
            </div>
            <p style="text-align:right;margin-bottom:0">
              <a href="/progress">Show Long Term Progress →</a>
            </p>
            <div id="emailReminderOptIn"></div>
          </div>
        )}

        {data.wordHistory && (
          <div class="dashboardPanel" style="text-align:center">
            <h2>Activity Monitor</h2>
            {data.wordHistory.today.notStarted && (
              <p>
                Translate new words or practice flashcards and your activity
                will light up here:
              </p>
            )}
            <div class="progressBar">
              {data.wordHistory.progressBars.map((bar, index) => (
                <i class={bar.classes} key={index}></i>
              ))}
              <span class={!data.wordHistory.today.notStarted ? "on" : ""}>
                &nbsp; {data.wordHistory.today.total} /{" "}
                {data.user.dailyPracticeTarget} today
              </span>
              <br />
              <i
                class={`icon-bolt icon-2x ${
                  data.wordHistory.today.goalReached ? "on" : ""
                }`}
              ></i>
              <span
                class={`streakCount ${
                  data.wordHistory.today.goalReached ? "on" : ""
                }`}
              >
                {data.wordHistory.streak.days} day streak
              </span>
            </div>

            <p style="text-align:center;margin-top:0">
              {data.wordHistory.today.wordsNeeded && (
                <>
                  {!data.wordHistory.streak.days &&
                    `Translate or recall ${data.wordHistory.today.wordsNeeded} more times to meet today's goal.`}
                  {data.wordHistory.streak.days &&
                    `Translate or recall ${data.wordHistory.today.wordsNeeded} more times today to continue your ${data.wordHistory.streak.days} day streak.`}
                </>
              )}
              {data.wordHistory.today.goalReached &&
                `You're on a ${data.wordHistory.streak.days} day streak. Come back tomorrow to keep it going!`}
            </p>

            <div class="progressChartView">
              <canvas class="weeklyChart"></canvas>
              <div class="weeklyChartLegend"></div>
            </div>
          </div>
        )}

        {data.karma && (
          <div class="dashboardPanel">
            <h2>
              {data.karma.karmaScore ? (
                <>
                  You have a karma score of {data.karma.karmaScore} from the{" "}
                  {data.karma.booksShared}{" "}
                  {data.user.currentLearningLanguageEnglishString} book
                  {data.karma.booksSharedPlural ? "s" : ""} you shared, thanks!
                </>
              ) : (
                <>Earn karma by sharing texts with the community</>
              )}
            </h2>
            <p>
              Earn karma every time another user likes one of your shared texts.
            </p>
          </div>
        )}
      </div>
    );
  }
}

export default DashboardPageView;
